import React, {useEffect, useMemo, useState} from 'react';
import {Box, Button} from '@mui/material';
import {KeySnapshot, KeyTransaction, KeyVault} from '../../api/Model';
import {today} from '../../util/DateUtil';
import {useAuthStore} from '../../app-layout/authStore';
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {useFormValidation} from "../../components/form-validation/useFormValidation";
import {executeKeyTransaction} from "../../api/KeyTransaction";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {MoveKeysDialog} from "./MoveKeysDialog";
import {useFilter} from "../../util/FilterSerializer";
import {
    filterKeySelectTable,
    KeySelectTableFilter,
    keySelectTableFilterFormSerializer
} from "../../components/key-select-table/KeySelectTableFilter";
import {KeySelectTable} from "../../components/key-select-table/KeySelectTable";
import {useVaultKeysStore} from "../../api/VaultKeysStore";
import {KeyOperationDeprecationWarning} from "../../components/deprecation-warning/DeprecationWarning";

export function MoveKeysPage({defaultKeyVault, keyVaults}: {
    defaultKeyVault: KeyVault,
    keyVaults: KeyVault[],
}) {
    const filterSerializer = useMemo(
        () => keySelectTableFilterFormSerializer(defaultKeyVault, keyVaults),
        [defaultKeyVault, keyVaults],
    );
    const [filter, setFilter] = useFilter(filterSerializer);
    const vaultKeys = useVaultKeysStore(state => state.remoteValue);
    const keys = useMemo(
        () => vaultKeys.map(currentKeys => currentKeys.allVaultKeys(filter.keyVault)),
        [filter.keyVault, vaultKeys],
    )
    const {
        formError,
        fieldErrors,
        validate: validateForm,
    } = useFormValidation<'key-vault'>();
    const [selectedKeys, setSelectedKeys] = useState(new Set<KeySnapshot>());
    const user = useAuthStore(state => state.user);

    useEffect(() => {
        setSelectedKeys(new Set());
    }, [keys]);

    const filteredKeys = useMemo(() => {
        return keys.map(allKeys => filterKeySelectTable(allKeys, filter));
    }, [keys, filter]);

    const handleMoveKeys = useFormDialogAction({
        title: 'Move Keys',
        description: 'Select the target key vault to move the selected keys.',
        submitButtonText: 'Move Keys',
        renderForm: MoveKeysDialog,
        action: async (openDialog) => {
            const isFormValid = validateForm({
                form: selectedKeys.size === 0 && 'Please select at least one key to move',
                "key-vault": !filter.keyVault && 'Please select a vault',
            });
            if (!filter.keyVault || !isFormValid) return;

            openDialog({
                initialValue: {targetVault: defaultKeyVault},
                onSubmit: async form => {
                    const targetVault = form.targetVault;
                    if (!targetVault) return;

                    // Create transactions to move keys to the target vault
                    const transactions: KeyTransaction[] = Array.from(selectedKeys, key => ({
                        keyNumber: key.key.keyNumber,
                        vault: targetVault,
                        date: today(),
                        newLocation: key.historyRecord.location,
                        notes: '',
                    }));

                    await executeKeyTransaction(transactions);

                    // Clear selection
                    setSelectedKeys(new Set());
                },
                successMessage: selectedKeys.size === 1
                    ? 'Moved 1 key successfully!'
                    : `Moved ${selectedKeys.size} keys successfully!`,
                errorMessage: 'Failed to move keys. Please try again.',
            });
        }
    }, [setSelectedKeys, filter.keyVault, selectedKeys, user]);

    return (
        <Page
            header="Move Keys"
            description={<KeyOperationDeprecationWarning/>}
            counter={<StorePageCount totalValue={keys} value={filteredKeys}/>}
            error={formError}
            actions={
                <Box display="flex" gap={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={selectedKeys.size === 0}
                        onClick={handleMoveKeys}
                    >
                        {selectedKeys.size === 1 ? (
                            `Move ${selectedKeys.size} Key`
                        ) : (
                            `Move ${selectedKeys.size} Keys`
                        )}
                    </Button>
                </Box>
            }
            filter={
                <KeySelectTableFilter form={filter} setForm={setFilter} fieldErrors={fieldErrors}/>
            }
        >
            <LoadingData
                totalValue={keys}
                value={filteredKeys}
                emptyMessage="No keys in this vault to move"
                noMatchMessage="No keys matching the search criteria"
                defaultErrorMessage="Failed to load keys. Please try again later."
            >{resolvedKeys =>
                <KeySelectTable keys={resolvedKeys} selectedKeys={selectedKeys} setSelectedKeys={setSelectedKeys}/>
            }</LoadingData>
        </Page>
    );
}